import { addMinutes, format, isValid, parseISO } from 'date-fns'
import { useTranslation } from './lang'
import { Unit } from './enums'

interface EnumSelectOption {
	id: number
	label: string
	value: any
}

export const EnumToSelectOption = (
	enumType: any,
	translationPrefix: string = '',
	allOption?: EnumSelectOption,
	idProperty?: boolean,
	removeOption?: string,
	labelLength?: number
) => {
	const { t } = useTranslation()

	let arr = (Object.keys(enumType) as Array<keyof typeof enumType>).map(
		(key, i) => ({
			id: idProperty ? enumType[key] : i,
			label: (translationPrefix
				? t(`${translationPrefix}.${enumType[key]}`)
				: enumType[key]
			).substring(0, labelLength),
			value: enumType[key],
		})
	)
	if (removeOption) {
		arr = arr.filter((option) => option.value !== removeOption)
	}
	if (allOption) {
		arr.unshift(allOption)
	}

	// PUT A SPECIFIC ELEMENT TO THE BACK, IF IT EXISTS
	const lastElement = 'Other'

	if (arr.some((item) => item.label === lastElement)) {
		const otherElementsArr = arr.filter(
			(item) => item.label === lastElement
		)
		const lastElementArr = arr.filter((item) => item.label !== lastElement)

		return lastElementArr.concat(otherElementsArr)
	}

	return arr
}

export function capitalize(str: string) {
	if (typeof str !== 'string' || str.length === 0) {
		return str
	}
	return str[0].toUpperCase() + str.slice(1)
}

export function capitalizeTitle(title: string | null | undefined) {
	if (!title) return ''

	const lowercaseWords = [
		'ghs',
		'a',
		'an',
		'the',
		'and',
		'but',
		'or',
		'on',
		'in',
		'with',
		'to',
		'of',
		'for',
		'by',
		'as',
		'at',
	]

	const acronyms = ['ghs', 'lot', 'sku', 'id', 'qr', 'cas', 'csv']

	return title
		.split(' ')
		.map((word, index, array) => {
			// Check if word is already uppercase
			if (word === word.toUpperCase() && word.length > 1) {
				return word
			}

			// Extract the word without special characters for checking
			const wordContent = word.replace(/[^a-zA-Z0-9]/g, '')

			// If no alphabetic content, return as is
			if (!wordContent) {
				return word
			}

			// Check if word content is an acronym
			if (acronyms.includes(wordContent.toLowerCase())) {
				// Preserve special characters but uppercase the letters
				return word.replace(/[a-zA-Z0-9]+/g, (match) =>
					match.toUpperCase()
				)
			}

			// Always capitalize the first and last word
			if (index === 0 || index === array.length - 1) {
				return word.replace(
					/[a-zA-Z0-9]+/g,
					(match) =>
						match.charAt(0).toUpperCase() +
						match.slice(1).toLowerCase()
				)
			}

			// Check if the core word content is in the list of lowercase words
			if (lowercaseWords.includes(wordContent.toLowerCase())) {
				return word.replace(/[a-zA-Z0-9]+/g, (match) =>
					match.toLowerCase()
				)
			}

			// Capitalize other words - preserve special characters but capitalize letters
			return word.replace(
				/[a-zA-Z0-9]+/g,
				(match) =>
					match.charAt(0).toUpperCase() + match.slice(1).toLowerCase()
			)
		})
		.join(' ')
		.replace(/\/(\w)/g, (match, letter) => `/${letter.toUpperCase()}`)
}

export function addTimezoneOffset(date: Date) {
	const offset = date.getTimezoneOffset()
	return addMinutes(date, -offset)
}

export const getDateString = (date: Date | string): string => {
	if (!date) return '/'

	if (typeof date !== 'string' && isValid(date)) {
		if (typeof navigator !== 'undefined') {
			return date
				.toLocaleDateString(navigator.language)
				.replace(/\//g, '.')
				.replace(/\s/g, '')
		} else {
			return format(date, 'd.M.yyyy')
		}
	} else {
		const newDate = new Date(date)
		if (isValid(newDate)) {
			if (typeof navigator !== 'undefined') {
				return newDate
					.toLocaleDateString(navigator.language)
					.replace(/\//g, '.')
					.replace(/\s/g, '')
			} else {
				return format(newDate, 'd.M.yyyy')
			}
		} else {
			return '/'
		}
	}
}

export const getTimeString = (data: number | Date) => {
	const date = typeof data === 'string' ? parseISO(data) : data

	if (!date) return '/'

	if (!isValid(date)) return '/'

	return format(date, 'HH:mm')
}

export const getDateTimeString = (date: Date): string => {
	if (!date) return '/'

	if (!isValid(date)) return '/'

	if (typeof navigator !== 'undefined') {
		const datePart = date
			.toLocaleDateString(navigator.language)
			.replace(/\//g, '.')
			.replace(/\s/g, '')
		const timePart = format(date, 'HH:mm')
		return `${datePart} ${timePart}`
	} else {
		return format(date, 'd.M.yyyy HH:mm')
	}
}

export function extractInitials(fullName: string) {
	if (!fullName) return ''

	const names = fullName.split(' ')
	const initials = names[0][0] + names[names.length - 1][0]
	return initials.toUpperCase()
}

export function getUnitTranslation(labelKey: string) {
	return Object.keys(Unit)
		.map((key) => key.toUpperCase())
		.includes(labelKey.toUpperCase())
		? `${'unit'}.${labelKey.toUpperCase()}`
		: capitalize(labelKey.toLowerCase())
}

export function isIPv4(str: string) {
	const ipv4Regex =
		/^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/
	return ipv4Regex.test(str)
}
